import React from "react";
import { Link } from "react-router-dom";
import { useAuth, hasPermission } from "../context/AuthContext";
import { useLookups } from "../context/LookupOptionsContext";
import { stackSampleIntakeUrl } from "../lib/workflowLinks";
import {
  CONTRACT_PHASES,
  gatePassSetsFarmer,
  getContractShortcut,
  isArrivalPhase,
  isOnArrivalDirectPurchase,
  isPreArrivalPhase,
  onContractPhaseChange,
  onGatePassChange,
  onPartyKindChange,
  onPurchaseTypeChange,
  partiesForKind,
  PARTY_BROKERAGE_COLLECTION_OPTIONS,
  PARTY_KIND_OPTIONS,
  PURCHASE_BROKERAGE_BASIS_OPTIONS,
  purchaseTypesForPhase,
  qualitySelectOptions,
  quickPresetSummaryLines,
} from "../lib/purchaseContractOptions";
import { WEIGHT_BASIS_OPTIONS } from "../lib/mandiSettlement";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { AlertTriangle } from "lucide-react";

export default function PurchaseContractFormFields({
  form,
  setForm,
  readOnly = false,
  contractNumber,
  brokers = [],
  farmers = [],
  suppliers = [],
  linkableLabs = [],
  linkableGatePasses = [],
  showPhaseSelector = true,
  /** When "shared", hide single-item quality/qty/rate/bardana (used for multi-item batch). */
  itemFieldsMode = "full",
  /** Set when opened via quick-create shortcut — hides preset fields. */
  quickPresetId = null,
  onClearQuickPreset = null,
}) {
  const { user } = useAuth();
  const { optionsFor } = useLookups();
  const isArrival = isArrivalPhase(form.contract_phase);
  const isPreArrival = isPreArrivalPhase(form.contract_phase);
  const onArrivalDirect = isOnArrivalDirectPurchase(form);
  const isStock = form.purchase_type === "STOCK_PURCHASE";
  const sampleAnalysed = form.stock_sample_analysed === "yes";
  const purchaseTypes = purchaseTypesForPhase(form.contract_phase);
  const paddyVarieties = optionsFor("paddy_variety");
  const qualityOptions = qualitySelectOptions(
    form.gate_pass_id,
    linkableGatePasses,
    paddyVarieties,
    [form.quality],
  );
  const partyOptions = partiesForKind(form.party_kind, farmers, suppliers);
  const bardanaOptions = optionsFor("bardana");
  const bardanaDeductionOptions = optionsFor("bardana_deduction");
  const deliveryOptions = optionsFor("delivery_condition");
  const cdPercentOptions = optionsFor("cd_percent");
  const paymentOptions = optionsFor("payment_condition");
  const quickPreset = getContractShortcut(quickPresetId);
  const quickMode = Boolean(quickPreset);
  const farmerFromGatePass = gatePassSetsFarmer(form) || quickMode;
  const selectedGatePass = linkableGatePasses.find((g) => String(g.id) === String(form.gate_pass_id));
  const yardPasses = farmerFromGatePass
    ? linkableGatePasses.filter((gp) => {
        if (!gp.party_id) return false;
        if (quickMode) {
          // Farmer shortcuts → farmers only; supplier shortcuts → suppliers only.
          if (form.party_kind === "SUPPLIER") return gp.party_kind === "SUPPLIER";
          return gp.party_kind === "FARMER";
        }
        return gp.party_kind === "FARMER" || gp.party_kind === "SUPPLIER";
      })
    : linkableGatePasses;

  if (readOnly) return null;

  return (
    <>
      {quickMode && (
        <div className="md:col-span-3 rounded-lg border border-teal-200 bg-teal-50/60 px-4 py-3" data-testid="pac-quick-preset-summary">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-teal-900">
                Quick create · Farmer · {quickPreset.label}
              </p>
              <ul className="text-xs text-muted-foreground mt-1 space-y-0.5 list-disc list-inside">
                {quickPresetSummaryLines(form, quickPresetId).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
            {onClearQuickPreset && (
              <button
                type="button"
                className="text-xs text-teal-800 underline underline-offset-2 shrink-0"
                onClick={onClearQuickPreset}
                data-testid="pac-show-all-fields"
              >
                Show all fields
              </button>
            )}
          </div>
        </div>
      )}

      {showPhaseSelector && !quickMode && (
        <Field label="Contract timing" required className="md:col-span-3">
          <Select
            value={form.contract_phase}
            onValueChange={(v) => setForm((f) => onContractPhaseChange(f, v))}
          >
            <SelectTrigger data-testid="pac-phase"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTRACT_PHASES.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      <Field label="Date" required>
        <Input
          type="date"
          value={form.contract_date}
          onChange={(e) => setForm({ ...form, contract_date: e.target.value })}
          required
          data-testid="pac-date"
        />
      </Field>
      {contractNumber ? (
        <Field label="Purchase contract number">
          <Input
            value={contractNumber}
            disabled
            readOnly
            className="bg-muted font-mono text-xs"
            data-testid="pac-contract-number"
          />
        </Field>
      ) : null}
      {!quickMode && !farmerFromGatePass && (
        <Field label="Party type" required>
          <Select
            value={form.party_kind || "FARMER"}
            onValueChange={(v) => setForm((f) => onPartyKindChange(f, v))}
            disabled={form.purchase_type === "FOR_FACTORY"}
          >
            <SelectTrigger data-testid="pac-party-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PARTY_KIND_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
      {!farmerFromGatePass && (
        <Field label="Party" required className="md:col-span-2">
          <Select
            value={form.party_id || "_none"}
            onValueChange={(v) => setForm({ ...form, party_id: v === "_none" ? "" : v })}
          >
            <SelectTrigger data-testid="pac-party"><SelectValue placeholder="Select party…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none" disabled>Select party…</SelectItem>
              {partyOptions.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.display_name}
                  {p.phone ? ` · ${p.phone}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {partyOptions.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              No active {form.party_kind === "SUPPLIER" ? "suppliers" : "farmers"} — add one under Parties.
            </p>
          )}
        </Field>
      )}

      {!quickMode && (
        <Field label="Purchase type" required>
          <Select
            value={form.purchase_type}
            onValueChange={(v) => setForm((f) => onPurchaseTypeChange(f, v, linkableGatePasses))}
          >
            <SelectTrigger data-testid="pac-purchase-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {purchaseTypes.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      {(isArrival || quickMode) && (
        <Field
          label={farmerFromGatePass ? "Party truck in yard" : "Gate pass code"}
          required
          className={farmerFromGatePass ? "md:col-span-3" : "md:col-span-2"}
        >
          <Select
            value={form.gate_pass_id || "_none"}
            onValueChange={(v) =>
              setForm((f) => onGatePassChange(f, v === "_none" ? "" : v, linkableGatePasses))
            }
          >
            <SelectTrigger data-testid="pac-gate-pass">
              <SelectValue placeholder={farmerFromGatePass ? "Select farmer/supplier truck in yard…" : "Select inward paddy pass…"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none" disabled>
                {farmerFromGatePass ? "Select farmer/supplier truck in yard…" : "Select IN-yard inward paddy pass…"}
              </SelectItem>
              {yardPasses.map((gp) => (
                <SelectItem key={gp.id} value={String(gp.id)}>
                  {farmerFromGatePass
                    ? `${gp.party_kind === "SUPPLIER" ? "Supplier" : "Farmer"} · ${gp.party_name} · ${gp.code} · ${gp.vehicle_number}`
                    : `${gp.code} · ${gp.vehicle_number}${gp.party_name ? ` · ${gp.party_name}` : ""}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {farmerFromGatePass && form.gate_pass_id && selectedGatePass && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="pac-gate-pass-farmer">
              {selectedGatePass.party_kind === "SUPPLIER" ? "Supplier" : "Farmer"} · Pass {selectedGatePass.code} · {selectedGatePass.vehicle_number}
            </p>
          )}
          {farmerFromGatePass && !form.gate_pass_id && (
            <p className="text-xs text-muted-foreground mt-1">
              Each option is a farmer or supplier currently in yard on an inward paddy pass. Party type is taken from that gate pass.
            </p>
          )}
          {farmerFromGatePass && yardPasses.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {quickMode && form.party_kind === "SUPPLIER"
                ? "No IN-yard inward passes linked to a supplier. Open GP-… at gate and set party to the supplier, or use New purchase contract."
                : quickMode
                  ? "No IN-yard inward passes linked to a farmer. Supplier trucks are hidden in this farmer preset — use Supplier quick create or New purchase contract."
                  : "No IN-yard inward passes with a linked farmer or supplier. At gate entry, set the party to the supplier (or farmer) before creating this contract."}
            </p>
          )}
          {!farmerFromGatePass && linkableGatePasses.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">No IN-yard inward paddy gate passes available.</p>
          )}
        </Field>
      )}

      {isPreArrival && isStock && (
        <div className="md:col-span-3 space-y-3 rounded-lg border border-amber-200 bg-amber-50/60 p-4">
          <p className="text-sm font-medium text-amber-900">Pre arrival stock purchase — sample status</p>
          <Field label="Is sample already analysed?" required>
            <Select
              value={form.stock_sample_analysed || "_none"}
              onValueChange={(v) =>
                setForm({
                  ...form,
                  stock_sample_analysed: v === "_none" ? "" : v,
                  stack_sample_lab_report_id: "",
                })
              }
            >
              <SelectTrigger data-testid="pac-sample-analysed"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none" disabled>Select…</SelectItem>
                <SelectItem value="yes">Yes — link lab report</SelectItem>
                <SelectItem value="no">No — send to supervisor TODO</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {sampleAnalysed && (
            <Field label="Stack sample lab report" required>
              <Select
                value={form.stack_sample_lab_report_id || "_none"}
                onValueChange={(v) =>
                  setForm({ ...form, stack_sample_lab_report_id: v === "_none" ? "" : v })
                }
              >
                <SelectTrigger data-testid="pac-lab-report"><SelectValue placeholder="Select lab report…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none" disabled>Select lab report…</SelectItem>
                  {linkableLabs.map((lab) => (
                    <SelectItem key={lab.id} value={String(lab.id)}>
                      {lab.sample_code || lab.intake_code} · stack {lab.stack_number || "—"} · {lab.verdict} · M {lab.moisture_pct}%
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          {form.stock_sample_analysed === "no" && (
            <div className="flex items-start gap-2 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Supervisor will receive a TODO to arrange stack sample intake and lab analysis.
                {hasPermission(user, "page.stack_samples") && (
                  <>
                    {" "}
                    <Link
                      to={stackSampleIntakeUrl(form.party_id)}
                      className="font-medium underline underline-offset-2 text-teal-800"
                    >
                      Open stack sample intake →
                    </Link>
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {itemFieldsMode === "full" && !quickMode && (
        <Field label="Quality" required className={onArrivalDirect ? "md:col-span-2" : undefined}>
          <Select
            value={form.quality || "_none"}
            onValueChange={(v) => setForm({ ...form, quality: v === "_none" ? "" : v })}
            disabled={onArrivalDirect && !form.gate_pass_id}
          >
            <SelectTrigger data-testid={onArrivalDirect ? "pac-quality-item" : "pac-quality"}>
              <SelectValue
                placeholder={
                  onArrivalDirect && !form.gate_pass_id ? "Select gate pass first" : "Select quality…"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none" disabled>
                {onArrivalDirect && !form.gate_pass_id ? "Select gate pass first" : "Select quality…"}
              </SelectItem>
              {qualityOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.gate_pass_id && (
            <p className="text-xs text-muted-foreground mt-1">
              Options include the linked gate pass variety and paddy grades from Dropdown choices.
            </p>
          )}
        </Field>
      )}
      {itemFieldsMode === "full" && !quickMode && !onArrivalDirect && (
        <Field label="Quantity (qtl)" required>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            required
            data-testid="pac-quantity"
          />
        </Field>
      )}
      {itemFieldsMode === "full" && !quickMode && (
        <Field label="Rate (₹/qtl)" required>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            required
            data-testid="pac-price"
          />
        </Field>
      )}
      {itemFieldsMode === "shared" && !quickMode && (
        <p className="md:col-span-3 text-xs text-muted-foreground -mt-1">
          Default bardana below applies to all item lines unless you add per-line overrides later on each contract.
        </p>
      )}
      {!quickMode && (
      <>
      <Field label="Bardana" required>
        <Select value={form.bardana} onValueChange={(v) => setForm({ ...form, bardana: v })}>
          <SelectTrigger data-testid="pac-bardana"><SelectValue /></SelectTrigger>
          <SelectContent>
            {bardanaOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {form.purchase_type !== "MANDI_PURCHASE" && (
        <Field label={form.purchase_type === "FOR_FACTORY" ? "Per qtl weight deduction" : "Bardana deduction"} required>
          <Select value={form.bardana_deduction} onValueChange={(v) => setForm({ ...form, bardana_deduction: v })}>
            <SelectTrigger data-testid="pac-bardana-deduction"><SelectValue /></SelectTrigger>
            <SelectContent>
              {bardanaDeductionOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
      {form.purchase_type === "FOR_FACTORY" && (
        <div className="md:col-span-2 space-y-1">
          <div className="flex items-center gap-2">
            <Checkbox
              id="pac-bardana-return"
              data-testid="pac-bardana-return"
              checked={form.bardana_return !== false}
              onCheckedChange={(v) => setForm({ ...form, bardana_return: Boolean(v) })}
            />
            <Label htmlFor="pac-bardana-return" className="text-sm font-normal cursor-pointer">
              Bardana is returned
            </Label>
          </div>
          <p className="text-xs text-muted-foreground">
            When this is checked, bag weight from the supplier chart is deducted first. The per qtl cut is then taken on the weight that remains. Leave it unchecked when the bags stay with the mill; only the per qtl cut applies, on the full net weight.
          </p>
        </div>
      )}
      {form.purchase_type === "MANDI_PURCHASE" && (
        <Field label="Material weight basis" required>
          <Select value={form.weight_basis || "FIXED_PER_BAG"} onValueChange={(v) => setForm({ ...form, weight_basis: v })}>
            <SelectTrigger data-testid="pac-weight-basis"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WEIGHT_BASIS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Fixed per bag: party weighbridge must meet bags × fixed kg + party bardana (from party chart).
          </p>
        </Field>
      )}
      <Field label="Delivery condition" required>
        <Select
          value={form.purchase_type === "FOR_FACTORY" ? "FOR" : form.delivery_condition}
          onValueChange={(v) => setForm({ ...form, delivery_condition: v })}
          disabled={form.purchase_type === "FOR_FACTORY"}
        >
          <SelectTrigger data-testid="pac-delivery"><SelectValue /></SelectTrigger>
          <SelectContent>
            {deliveryOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Freight rate (₹/qtl)">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={form.freight_rate_per_qtl}
          onChange={(e) => setForm({ ...form, freight_rate_per_qtl: e.target.value })}
          placeholder={form.delivery_condition === "FOR" ? "Debit party if paid to truck" : "Advice on truck load"}
          data-testid="pac-freight-rate"
        />
      </Field>

      <div className="md:col-span-3 space-y-3 rounded-lg border p-4 bg-muted/50/80">
        <div className="flex items-center gap-2">
          <Checkbox
            id="pac-cd"
            checked={form.cd_applicable}
            onCheckedChange={(v) => setForm({ ...form, cd_applicable: Boolean(v) })}
          />
          <Label htmlFor="pac-cd" className="text-sm font-normal cursor-pointer">CD applicable</Label>
        </div>
        {form.cd_applicable && (
          <Field label="CD %">
            <Select value={form.cd_percent} onValueChange={(v) => setForm({ ...form, cd_percent: v })}>
              <SelectTrigger className="max-w-[140px]" data-testid="pac-cd-percent"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cdPercentOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}
      </div>

      <div className="md:col-span-3 space-y-3 rounded-lg border p-4 bg-muted/50/80">
        <div className="flex items-center gap-2">
          <Checkbox
            id="pac-broker"
            checked={form.broker_applicable}
            onCheckedChange={(v) => setForm({ ...form, broker_applicable: Boolean(v) })}
          />
          <Label htmlFor="pac-broker" className="text-sm font-normal cursor-pointer">Broker involved</Label>
        </div>
        {form.broker_applicable && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Broker (party)" required>
                <Select
                  value={form.broker_party_id || "_none"}
                  onValueChange={(v) => setForm({ ...form, broker_party_id: v === "_none" ? "" : v })}
                >
                  <SelectTrigger data-testid="pac-broker-party"><SelectValue placeholder="Select broker…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none" disabled>Select broker…</SelectItem>
                    {brokers.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Legacy flat brokerage (optional)">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.brokerage}
                  onChange={(e) => setForm({ ...form, brokerage: e.target.value })}
                  placeholder="Use split terms below when possible"
                  data-testid="pac-brokerage"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-lg border bg-card p-4 space-y-3">
                <div className="text-sm font-semibold text-foreground">Our-side brokerage (mill cost)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Basis">
                    <Select
                      value={form.our_side_brokerage_basis || "NONE"}
                      onValueChange={(v) => setForm({ ...form, our_side_brokerage_basis: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PURCHASE_BROKERAGE_BASIS_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Rate">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.our_side_brokerage_rate}
                      onChange={(e) => setForm({ ...form, our_side_brokerage_rate: e.target.value })}
                      disabled={!form.our_side_brokerage_basis || form.our_side_brokerage_basis === "NONE"}
                    />
                  </Field>
                </div>
              </div>

              <div className="rounded-lg border bg-card p-4 space-y-3">
                <div className="text-sm font-semibold text-foreground">Party-side brokerage</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Basis">
                    <Select
                      value={form.party_side_brokerage_basis || "NONE"}
                      onValueChange={(v) => setForm({ ...form, party_side_brokerage_basis: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PURCHASE_BROKERAGE_BASIS_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Rate">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.party_side_brokerage_rate}
                      onChange={(e) => setForm({ ...form, party_side_brokerage_rate: e.target.value })}
                      disabled={!form.party_side_brokerage_basis || form.party_side_brokerage_basis === "NONE"}
                    />
                  </Field>
                </div>
                <Field label="Collection method">
                  <Select
                    value={form.party_side_collection_method || "DEDUCT_FROM_LEDGER"}
                    onValueChange={(v) => setForm({ ...form, party_side_collection_method: v })}
                    disabled={!form.party_side_brokerage_basis || form.party_side_brokerage_basis === "NONE"}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PARTY_BROKERAGE_COLLECTION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          </div>
        )}
      </div>

      <Field label="Payment condition" required>
        <Select
          value={form.payment_condition}
          onValueChange={(v) => setForm({ ...form, payment_condition: v, payment_condition_other: "" })}
        >
          <SelectTrigger data-testid="pac-payment"><SelectValue /></SelectTrigger>
          <SelectContent>
            {paymentOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {form.payment_condition === "OTHER" && (
        <Field label="Payment details" required className="md:col-span-2">
          <Input
            value={form.payment_condition_other}
            onChange={(e) => setForm({ ...form, payment_condition_other: e.target.value })}
            placeholder="Describe payment terms…"
            required
            data-testid="pac-payment-other"
          />
        </Field>
      )}
      <Field label="Quality conditions (optional)" className="md:col-span-3">
        <Textarea
          value={form.quality_conditions}
          onChange={(e) => setForm({ ...form, quality_conditions: e.target.value })}
          rows={2}
          placeholder="Optional moisture, broken, admixture limits…"
          data-testid="pac-quality-conditions"
        />
      </Field>
      </>
      )}
    </>
  );
}

function Field({ label, required, children, className }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
